import React, { useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { formatRelativeDate } from '@/utils/formatRelativeDate';
import { paginate } from '@/utils/transfers';
import { listMySalesApi, type ApiCreditSale } from '@/api/vendo';

const PAGE_SIZE = 10;

/**
 * The current user's completed credit SALES — fulfilled inquiries where
 * they were the selling distributor (reseller sales join once
 * reseller-as-seller ships). Distinct from CreditTransfersTable on
 * purpose: sales are written as CreditLedger rows by inquiry-fulfill,
 * never as CreditTransfer, so the wallet-transfers list can't show them
 * (that was the "Transfer Credit tab is empty" bug). Shared by the
 * Distributor and Reseller pages.
 */
const SoldCreditsTable: React.FC = () => {
   const { data, isLoading } = useQuery({
      queryKey: ['vendo', 'my-sales'],
      queryFn: listMySalesApi,
   });
   const sales: ApiCreditSale[] = data?.sales ?? [];
   const [page, setPage] = useState(1);

   if (isLoading) return <SkeletonLines lines={3} />;
   if (sales.length === 0)
      return (
         <Empty>
            No sold credits yet. When you fulfill a buyer&apos;s credit
            inquiry, the sale appears here.
         </Empty>
      );

   const { pageRows: rows, totalPages, safePage } = paginate(
      sales,
      page,
      PAGE_SIZE
   );

   return (
      <>
         <Table>
            <Head>
               <Cell>Buyer</Cell>
               <Cell $right>Credits</Cell>
               <Cell $right>Price / credit</Cell>
               <Cell $right>Total</Cell>
               <Cell $right>When</Cell>
            </Head>
            {rows.map((s) => (
               <Row key={s.id}>
                  <Cell>
                     <BuyerName>{s.buyerName || s.buyerEmail || '—'}</BuyerName>
                     {s.buyerName && s.buyerEmail && (
                        <BuyerEmail>{s.buyerEmail}</BuyerEmail>
                     )}
                  </Cell>
                  <CellRight $color={colors.SUCCESS}>
                     {s.credits.toLocaleString()}
                  </CellRight>
                  <CellRight $muted>
                     ₱{s.pricePerCredit.toLocaleString()}
                  </CellRight>
                  <CellRight>₱{s.total.toLocaleString()}</CellRight>
                  <CellRight $muted>
                     {formatRelativeDate(s.fulfilledAt)}
                  </CellRight>
               </Row>
            ))}
         </Table>
         {totalPages > 1 && (
            <Pager>
               <PagerBtn
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => setPage(safePage - 1)}
               >
                  ← Prev
               </PagerBtn>
               <PagerInfo>
                  {safePage} / {totalPages}
               </PagerInfo>
               <PagerBtn
                  type="button"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(safePage + 1)}
               >
                  Next →
               </PagerBtn>
            </Pager>
         )}
      </>
   );
};

export default SoldCreditsTable;

const Muted = styled.p`
   font-size: 13px;
   color: ${colors.GRAY5};
   padding: 12px 0;
`;
const Empty = styled.div`
   padding: 24px 16px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   background: ${colors.WHITE};
   border: 1px dashed ${colors.GRAY12};
   border-radius: 12px;
`;
const Table = styled.div`
   display: flex;
   flex-direction: column;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   overflow: hidden;
`;
const gridCols = '1.8fr 0.7fr 0.9fr 0.9fr 0.9fr';
const Head = styled.div`
   display: grid;
   grid-template-columns: ${gridCols};
   gap: 10px;
   padding: 9px 12px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
   font-size: 10px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.GRAY5};
`;
const Row = styled.div`
   display: grid;
   grid-template-columns: ${gridCols};
   gap: 10px;
   padding: 10px 12px;
   align-items: center;
   border-bottom: 1px solid ${colors.GRAY14};
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   &:last-child {
      border-bottom: none;
   }
`;
const Cell = styled.div<{ $right?: boolean }>`
   min-width: 0;
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   word-break: break-word;
`;
const CellRight = styled.div<{ $color?: string; $muted?: boolean }>`
   text-align: right;
   white-space: nowrap;
   font-weight: ${({ $muted }) => ($muted ? 500 : 800)};
   color: ${({ $color, $muted }) =>
      $color ?? ($muted ? colors.GRAY5 : colors.MAIN_DARK)};
`;
const BuyerName = styled.div`
   font-weight: 700;
`;
const BuyerEmail = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const Pager = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: 10px;
   padding-top: 8px;
`;
const PagerBtn = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 5px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 7px;
   background: ${colors.WHITE};
   color: ${colors.GRAY3};
   cursor: pointer;
   &:disabled {
      opacity: 0.4;
      cursor: default;
   }
`;
const PagerInfo = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
